import * as THREE from "three";
import CameraControls from "camera-controls";
import {
  distinctUntilChanged,
  filter,
  groupBy,
  mergeMap,
  pluck,
} from "rxjs/operators";

CameraControls.install({ THREE: THREE });

export const CameraSystem = (world, { width = 640, height = 480 } = {}) => {
  const camera = (world.camera = new THREE.PerspectiveCamera(
    75,
    width / height,
    0.05,
    100
  ));
  const cameraControls = (world.cameraControls = new CameraControls(
    camera,
    world.renderer.domElement
  ));

  cameraControls.addEventListener("controlstart", (e) => world.change$.next());
  cameraControls.addEventListener("awake", (e) => world.change$.next());
  cameraControls.addEventListener("control", (e) => world.change$.next());
  cameraControls.minDistance = 0;
  cameraControls.maxDistance = 10;
  cameraControls.minZoom = 1;
  cameraControls.maxZoom = 5;

  cameraControls.addEventListener("update", (e) => {
    const position = world.camera.position.toArray();
    const target = world.cameraControls.getTarget().toArray();
    world.camera$.next({
      position,
      target,
    });
  });

  cameraControls.setLookAt(5, 2.5, 5, 0, 0, 0, false);

  world.size$.subscribe((size) => {
    if (size) {
      const { width = 800, height = 600 } = size;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }
  });

  // subscribe stream
  world.data.entity$
    .pipe(
      groupBy(
        (entity) => entity.id,
        (entity) => entity
      ),
      mergeMap((group$) =>
        group$.pipe(filter((entity) => entity.hasOwnProperty("camera")))
      )
    )
    .subscribe((entity) => {
      const { position, target } = entity.camera;

      position &&
        target &&
        cameraControls.setLookAt(
          position[0],
          position[1],
          position[2],
          target[0],
          target[1],
          target[2],
          true
        );

      world.change$.next();
    });

  // rotate camera
  world.data.state$
    .pipe(
      pluck("three", "rotateCount"),
      distinctUntilChanged(),
      filter((v) => v !== undefined)
    )
    .subscribe((value) => {
      const segments = 4;
      const segment = Math.PI / segments;
      const azimuthAngle =
        Math.floor(cameraControls.azimuthAngle / segment + segment / 2) *
          segment +
        segment;

      const view = world.store.getState()?.context?.tractor?.state?.view;

      if (view) {
        const target = world.store
          .getState()
          ?.context?.tractor?.property?.view?.options?.find(
            (option) => option.value === view
          )?.camera?.target || [0, 0, 0];

        cameraControls.moveTo(...target, true);
      }
      // todo get target from selected view
      cameraControls.rotateTo(azimuthAngle, Math.PI / 2.2, true);

      world.change$.next();
    });

  // Custom functionality for inside camera.
  // 360 viewer behavior
  world.data.state$
    .pipe(
      pluck("context", "tractor", "state", "view"),
      distinctUntilChanged(),
      filter((v) => v !== undefined)
    )
    .subscribe((value) => {
      if (value === "interior") {
        cameraControls.mouseButtons.wheel = CameraControls.ACTION.ZOOM;
        cameraControls.touches.two = CameraControls.ACTION.TOUCH_ZOOM;
        cameraControls.truckSpeed = 4;
      } else {
        cameraControls.mouseButtons.wheel = CameraControls.ACTION.DOLLY;
        cameraControls.touches.two = CameraControls.ACTION.TOUCH_DOLLY_TRUCK;
        cameraControls.truckSpeed = 2;
        cameraControls.zoomTo(1);
      }
    });
};

export default CameraSystem;
